(() => {
class PdpGallery {
  constructor(gallery) {
    this.gallery = gallery
    this.modal = document.getElementById('media-modal')
    this.productInfo = this.gallery?.closest('product-info')
    this.modalSplide = null
    this.mobileSlider = null
    this.mobileThumbSlider = null
    this.lastTrigger = null
    this.modalFocusTrap = window.ThemeFocusTrap && this.modal
      ? new window.ThemeFocusTrap(this.modal, {
          initialFocus: '[data-media-modal-close]',
          onEscape: () => this.closeModal()
        })
      : null
    this.hasDismissedSwipeHint = false
    this.variantSelectorListeners = []

    if (!this.gallery) return

    this.gallery.pdpGallery = this

    this.desktopItems = Array.from(this.gallery.querySelectorAll('[data-gallery-item]'))
    this.mobileSlides = Array.from(this.gallery.querySelectorAll('[data-gallery-mobile-slide]'))
    this.mobileThumbs = Array.from(this.gallery.querySelectorAll('[data-gallery-mobile-thumb]'))
    this.mobileElement = this.gallery.querySelector('[data-gallery-mobile]')
    this.mobileThumbElement = this.gallery.querySelector('[data-gallery-mobile-thumb-slider]')
    this.counter = this.gallery.querySelector('[data-gallery-counter]')
    this.swipeHint = this.gallery.querySelector('[data-gallery-swipe-hint]')

    this.bindGalleryImages()
    this.bindMobileThumbs()
    this.bindVariantChanges()
    this.bindModal()
    this.initMobileThumbSlider()
    this.initMobileSlider()
    this.observeVideos()
  }

  bindGalleryImages() {
    this.gallery.querySelectorAll('[data-gallery-open-modal]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('[data-gallery-item], [data-gallery-mobile-slide]')
        this.lastTrigger = button
        this.openModal(Number(item?.dataset.index || 0))
      })
    })
  }

  bindMobileThumbs() {
    this.mobileThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.setActiveMobileMedia(Number(thumb.dataset.index || 0), true)
      })
    })
  }

  bindVariantChanges() {
    const selectorScope = this.productInfo || this.gallery
    selectorScope.querySelectorAll('input[name="id"], select[name="id"]').forEach((selector) => {
      const handler = () => this.focusVariantMedia(selector.value)
      selector.addEventListener('change', handler)
      this.variantSelectorListeners.push({ selector, handler })
    })

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        this.focusVariantMedia(data?.variant?.id)
      })
    }
  }

  focusVariantMedia(variantId) {
    if (!variantId || !window.productColorVariantMap) return

    const color = window.productColorVariantMap[variantId]
    if (!color) return

    if (window.matchMedia('(min-width: 992px)').matches) {
      const desktopTarget = this.desktopItems.find((item) => item.dataset.color === color)
      desktopTarget?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      return
    }

    const mobileTarget = this.mobileSlides.find((slide) => slide.dataset.color === color)
    if (mobileTarget) this.setActiveMobileMedia(Number(mobileTarget.dataset.index || 0), true)
  }

  initMobileThumbSlider() {
    if (!this.mobileThumbElement || typeof Splide !== 'function') return

    this.mobileThumbSlider = new Splide(this.mobileThumbElement, {
      type: 'slide',
      fixedWidth: 'var(--space-7)',
      fixedHeight: 'var(--space-7)',
      gap: 'var(--space-1)',
      perMove: 1,
      arrows: false,
      pagination: false,
      drag: true,
      speed: 360,
      trimSpace: true,
      omitEnd: true
    }).mount()
  }

  initMobileSlider() {
    if (!this.mobileElement || typeof Splide !== 'function') return

    this.mobileSlider = new Splide(this.mobileElement, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      arrows: false,
      pagination: false,
      speed: 420,
      drag: this.mobileSlides.length > 1
    })

    this.mobileSlider.on('mounted moved', () => {
      this.setActiveMobileMedia(this.mobileSlider.index || 0, false)
    })

    this.mobileSlider.on('drag', () => this.dismissSwipeHint())
    this.mobileSlider.mount()
  }

  setActiveMobileMedia(index, syncSlider) {
    this.mobileThumbs.forEach((thumb) => {
      const isActive = Number(thumb.dataset.index || 0) === index
      thumb.classList.toggle('is-active', isActive)
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false')
    })

    if (syncSlider && this.mobileSlider && this.mobileSlider.index !== index) {
      this.mobileSlider.go(index)
    }

    if (this.mobileThumbSlider && this.mobileThumbSlider.index !== index) {
      this.mobileThumbSlider.go(index)
    }

    this.mobileSlides.forEach((slide) => {
      if (Number(slide.dataset.index || 0) === index) return
      slide.querySelectorAll('video').forEach((video) => video.pause())
    })

    this.updateCounter(index)
    this.updateSwipeHint(index)
  }

  updateCounter(index) {
    if (this.counter) this.counter.textContent = `${index + 1} of ${this.mobileSlides.length}`
  }

  updateSwipeHint(index) {
    if (!this.swipeHint) return
    this.swipeHint.classList.toggle('is-hidden', this.hasDismissedSwipeHint || index > 1)
  }

  dismissSwipeHint() {
    this.hasDismissedSwipeHint = true
    this.updateSwipeHint(this.mobileSlider?.index || 0)
  }

  bindModal() {
    if (!this.modal) return

    this.modal.querySelectorAll('[data-media-modal-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeModal())
    })

    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeModal()
    })

  }

  observeVideos() {
    const videos = Array.from(this.gallery.querySelectorAll('video'))
    if (!videos.length || typeof IntersectionObserver !== 'function') return

    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          entry.target.pause()
        }
      })
    }, { threshold: 0.25 })

    videos.forEach((video) => this.videoObserver.observe(video))
  }

  openModal(mediaIndex) {
    if (!this.modal) return

    const slides = Array.from(this.modal.querySelectorAll('[data-media-index]'))
    const startIndex = Math.max(slides.findIndex((slide) => Number(slide.dataset.mediaIndex) === mediaIndex), 0)

    this.modal.classList.add('active')
    this.modal.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'
    this.modalFocusTrap?.activate(this.lastTrigger)

    if (typeof Splide === 'function') {
      this.modalSplide = new Splide(this.modal, {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        arrows: slides.length > 1,
        pagination: false,
        drag: slides.length > 1,
        speed: 360,
        start: startIndex
      }).mount()
    }

  }

  closeModal() {
    if (!this.modal) return

    this.modal.classList.remove('active')
    this.modal.setAttribute('aria-hidden', 'true')
    document.body.style.overflow = ''
    this.modalFocusTrap?.deactivate()

    if (this.modalSplide) {
      this.modalSplide.destroy(true)
      this.modalSplide = null
    }

    this.lastTrigger = null
  }

  destroy() {
    this.mobileSlider?.destroy(true)
    this.mobileThumbSlider?.destroy(true)
    this.modalSplide?.destroy(true)
    this.videoObserver?.disconnect()
    this.variantChangeUnsubscriber?.()
    this.variantSelectorListeners.forEach(({ selector, handler }) => {
      selector.removeEventListener('change', handler)
    })
    this.modalFocusTrap?.deactivate({ restoreFocus: false })

    if (this.gallery) this.gallery.pdpGallery = null
  }
}

const initPdpGalleries = (scope = document) => {
  scope.querySelectorAll('[data-pdp-gallery]').forEach((gallery) => {
    if (gallery.pdpGallery) return
    new PdpGallery(gallery)
  })
}

window.initPdpGalleries = initPdpGalleries
initPdpGalleries()

if (!window.pdpGalleryLifecycleBound) {
  document.addEventListener('shopify:section:load', (event) => {
    window.initPdpGalleries?.(event.target)
  })
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-pdp-gallery]').forEach((gallery) => {
      gallery.pdpGallery?.destroy()
    })
  })
  window.pdpGalleryLifecycleBound = true
}
})()
